import {
  default as React,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type SyntheticEvent,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useCurrentUserEmail } from '../components/catalog/microapps/hooks';
import {
  ACCESS_KIND_STYLES,
  AccessPickerGroupHeader,
  AccessPickerOption,
  accessChipClass,
  useAccessPickerStyles,
  type AccessKind,
} from './accessPickerUi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SearchOption {
  email: string;
  displayName: string;
  source: 'internal' | 'external';
  /** Autocomplete group header label */
  group: string;
}

const INTERNAL_GROUP = 'WellSky Users';
const EXTERNAL_GROUP = 'External Users';
const INVITE_GROUP = 'Invite by Email';

/**
 * Scaffolder field for selecting individual users for public app access.
 *
 * - Internal users (@wellsky.com): searched from Backstage catalog (User entities)
 * - External users: searched from WellSky Identity CIAM via POST /api/microapps-api/ciam/users/search
 *
 * Internal users are added to the CIAM Application Group without an invite (they use
 * Inbound Federation → Enterprise Okta SSO). External users receive an activation email
 * if they do not already have a CIAM account.
 *
 * Returns: string[] — array of email addresses (shown on review step, used for provisioning).
 *
 * Usage in template YAML:
 *   ui:field: PublicAccessUserPicker
 */
export const PublicAccessUserPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  formContext,
}: FieldExtensionComponentProps<string[]>) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const classes = useAccessPickerStyles();

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Display name map — email -> displayName, kept in local state for chip rendering.
  // Not persisted in formData; chips show the email if displayName is unknown after remount.
  const [displayNames, setDisplayNames] = useState<Map<string, string>>(
    new Map(),
  );

  // formData is string[] of emails.
  const selected: string[] = useMemo(
    () => (Array.isArray(formData) ? formData : []),
    [formData],
  );
  const selectedEmails = useMemo(
    () => new Set(selected.map(e => e.toLowerCase())),
    [selected],
  );

  // Reads the email from the cached JWT — fast (no network round-trip).
  const currentUserEmail = useCurrentUserEmail();
  const currentUserEmailLower = currentUserEmail
    ? currentUserEmail.toLowerCase()
    : null;

  /**
   * Protected owner set:
   *  - Redeploy: ownerEmails pre-filled from formContext (skystage.io/owner-emails annotation)
   *  - Fresh deploy: falls back to the current logged-in user only
   * Mirrors the pattern used in AccessControlField.
   */
  const effectiveOwnerEmails = useMemo<Set<string>>(() => {
    const fromContext = Array.isArray(formContext?.formData?.ownerEmails)
      ? (formContext.formData.ownerEmails as string[]).map((e: string) =>
          e.toLowerCase(),
        )
      : [];
    if (fromContext.length > 0) return new Set(fromContext);
    const base = currentUserEmailLower ? [currentUserEmailLower] : [];
    return new Set(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext?.formData?.ownerEmails, currentUserEmailLower]);

  // Seed the logged-in user into the list exactly once per mount.
  // Uses initializedRef so remounts (which reset useState) don't cause duplicate adds.
  const initializedRef = useRef(false);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (!currentUserEmail || initializedRef.current) return;
    initializedRef.current = true;
    const toAdd = [currentUserEmail].filter(
      e => !selectedRef.current.some(s => s.toLowerCase() === e.toLowerCase()),
    );
    if (toAdd.length > 0) {
      onChange([...toAdd, ...selectedRef.current]);
    }
  }, [currentUserEmail, onChange]);

  const isOwner = (email: string) =>
    effectiveOwnerEmails.has(email.toLowerCase());

  // Non-blocking: populate the owner's display name for chip rendering.
  useEffect(() => {
    if (!currentUserEmail) return;
    identityApi
      .getProfileInfo()
      .then(profile => {
        if (profile.displayName && profile.email) {
          setDisplayNames(prev => {
            const next = new Map(prev);
            next.set(
              profile.email!.toLowerCase(),
              profile.displayName ?? profile.email!,
            );
            return next;
          });
        }
      })
      .catch(() => {});
  }, [currentUserEmail, identityApi]);

  const backendUrl = config.getString('backend.baseUrl');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchUsers = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (q.length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      setFetchError(null);

      try {
        const { token } = await identityApi.getCredentials();
        const authHeader: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};

        // Search internal WellSky users via enterprise Okta (same endpoint as AccessControlField).
        // Returns 503 in local dev (Okta private key not configured) — caught silently below.
        const oktaSearch = fetch(
          `${backendUrl}/api/microapps-api/access/users/search?q=${encodeURIComponent(
            q,
          )}`,
          { headers: authHeader, credentials: 'include' },
        )
          .then(async res => {
            if (!res.ok) return [] as SearchOption[];
            const data = (await res.json()) as {
              users: Array<{
                id: string;
                profile: {
                  login: string;
                  email: string;
                  firstName: string;
                  lastName: string;
                  displayName?: string;
                };
              }>;
            };
            return (data.users ?? []).slice(0, 10).map(u => ({
              email: u.profile.email,
              displayName:
                u.profile.displayName ??
                (`${u.profile.firstName} ${u.profile.lastName}`.trim() ||
                  u.profile.login),
              source: 'internal' as const,
              group: INTERNAL_GROUP,
            }));
          })
          .catch(() => [] as SearchOption[]);

        // Search external users from CIAM (excludes @wellsky.com server-side)
        const ciamSearch = fetch(
          `${backendUrl}/api/microapps-api/ciam/users/search`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader },
            credentials: 'include',
            body: JSON.stringify({ search: q, pageSize: 10 }),
          },
        )
          .then(async res => {
            if (!res.ok) throw new Error(`CIAM search failed: ${res.status}`);
            const data = (await res.json()) as {
              items: Array<{
                id: string;
                email: string;
                displayName: string;
                status: string;
              }>;
            };
            return (data.items ?? []).map(u => ({
              email: u.email,
              displayName: u.displayName,
              source: 'external' as const,
              group: EXTERNAL_GROUP,
            }));
          })
          .catch(() => [] as SearchOption[]);

        const [internal, external] = await Promise.all([
          oktaSearch,
          ciamSearch,
        ]);

        // Deduplicate and filter already-selected
        const all = [...internal, ...external].filter(
          u => !selectedEmails.has(u.email.toLowerCase()),
        );
        setOptions(all);
      } catch (_err) {
        setFetchError('Failed to search users. Try again.');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [
      backendUrl,
      identityApi,
      selectedEmails,
      setLoading,
      setFetchError,
      setOptions,
    ],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void searchUsers(inputValue);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, searchUsers]);

  const addEmail = useCallback(
    (email: string, displayName?: string) => {
      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) return;
      if (selectedEmails.has(trimmed.toLowerCase())) return;
      if (displayName) {
        setDisplayNames(prev => {
          const next = new Map(prev);
          next.set(trimmed.toLowerCase(), displayName);
          return next;
        });
      }
      onChange([...selected, trimmed]);
      setInputValue('');
      setOptions([]);
    },
    [selected, selectedEmails, onChange],
  );

  const handleSelect = useCallback(
    (_: SyntheticEvent, opt: SearchOption | string | null) => {
      if (!opt) return;
      if (typeof opt === 'string') {
        // freeSolo — user pressed Enter on a raw email
        addEmail(opt);
      } else {
        // Don't store the "Add "email"" prefix as the display name — that
        // label is only meaningful in the dropdown, not on the chip.
        const name = opt.displayName.startsWith('Add "')
          ? undefined
          : opt.displayName;
        addEmail(opt.email, name);
      }
    },
    [addEmail],
  );

  const removeUser = useCallback(
    (email: string) => {
      if (effectiveOwnerEmails.has(email.toLowerCase())) return;
      onChange(selected.filter(e => e !== email));
    },
    [selected, onChange, effectiveOwnerEmails],
  );

  const hasError = rawErrors !== undefined && rawErrors.length > 0;
  const isValidEmail = EMAIL_RE.test(inputValue.trim());
  const noOptionsText =
    inputValue.trim().length < 2
      ? 'Type at least 2 characters to search'
      : fetchError ??
        (isValidEmail
          ? 'No existing users found — press Enter to invite by email'
          : 'No users found');

  // Redeploy without the current-access snapshot: the Redeploy button could not
  // load the app's users, so the list below is incomplete and the backend will
  // only add users (it never removes anyone it cannot see).
  const currentUsersUnknown =
    formContext?.formData?.actionType === 'redeploy' &&
    !Array.isArray(formContext?.formData?.previousAllowedUsers);

  return (
    <FormControl fullWidth sx={{ mt: 2 }} error={hasError}>
      {currentUsersUnknown && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The app&apos;s current users could not be loaded, so they are not
          listed here. Users you add will get access; to remove someone, use the{' '}
          <strong>Access tab</strong>.
        </Alert>
      )}
      {selected.length > 0 && (
        <Box className={classes.chipRow}>
          {selected.map(email => {
            const owner = isOwner(email);
            const isInternal = email.toLowerCase().endsWith('@wellsky.com');
            const displayName = displayNames.get(email.toLowerCase());
            const ownerSuffix = owner ? ' · Owner' : '';
            const chipLabel = displayName
              ? `${displayName} (${email})${ownerSuffix}`
              : `${email}${ownerSuffix}`;
            let chipTitle: string;
            if (owner) {
              chipTitle = 'Owner — cannot be removed';
            } else if (isInternal) {
              chipTitle = 'WellSky user — uses SSO, no invite sent';
            } else {
              chipTitle = 'External user — activation email sent if new';
            }
            return (
              <Chip
                key={email}
                label={chipLabel}
                onDelete={owner ? undefined : () => removeUser(email)}
                className={accessChipClass(
                  classes,
                  isInternal ? 'internal' : 'external',
                )}
                size="small"
                icon={
                  isInternal ? (
                    <BusinessIcon fontSize="small" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )
                }
                title={chipTitle}
              />
            );
          })}
        </Box>
      )}

      <Autocomplete<SearchOption, false, false, true>
        freeSolo
        options={options}
        groupBy={opt => (typeof opt === 'string' ? '' : opt.group)}
        getOptionLabel={(opt: SearchOption | string) =>
          typeof opt === 'string' ? opt : `${opt.displayName} (${opt.email})`
        }
        isOptionEqualToValue={(opt, val) =>
          typeof opt !== 'string' &&
          typeof val !== 'string' &&
          opt.email.toLowerCase() === val.email.toLowerCase()
        }
        inputValue={inputValue}
        onInputChange={(_, val, reason) => {
          if (reason !== 'reset') setInputValue(val);
        }}
        onChange={handleSelect}
        value={null}
        loading={loading}
        filterOptions={(opts, { inputValue: filterInput }) => {
          const trimmed = filterInput.trim();
          if (
            EMAIL_RE.test(trimmed) &&
            !selectedEmails.has(trimmed.toLowerCase()) &&
            !opts.some(o => o.email.toLowerCase() === trimmed.toLowerCase())
          ) {
            return [
              ...opts,
              {
                email: trimmed,
                displayName: `Add "${trimmed}"`,
                source: 'external' as const,
                group: INVITE_GROUP,
              },
            ];
          }
          return opts;
        }}
        noOptionsText={noOptionsText}
        loadingText="Searching users…"
        className={classes.input}
        slotProps={{ paper: { className: classes.paper } }}
        renderInput={params => (
          <TextField
            {...params}
            label={schema?.title ?? 'Individual Users'}
            required={required && selected.length === 0}
            error={hasError}
            placeholder="Search by name or paste an email address..."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderGroup={params => (
          <AccessPickerGroupHeader
            key={params.key}
            classes={classes}
            label={params.group}
            count={
              params.group === INVITE_GROUP
                ? 0
                : React.Children.count(params.children)
            }
          >
            {params.children}
          </AccessPickerGroupHeader>
        )}
        renderOption={(props, opt) => {
          if (typeof opt === 'string') return null;
          const { key: _key, ...liProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
          const isInvite = opt.group === INVITE_GROUP;
          let kind: AccessKind = 'external';
          if (isInvite) kind = 'invite';
          else if (opt.source === 'internal') kind = 'internal';
          return (
            <AccessPickerOption
              key={opt.email}
              liProps={liProps}
              classes={classes}
              kind={kind}
              primary={isInvite ? `Invite ${opt.email}` : opt.displayName}
              secondary={
                isInvite
                  ? 'Sends an activation email if they are new to WellSky Identity'
                  : opt.email
              }
            />
          );
        }}
      />

      {hasError && (
        <FormHelperText error>{rawErrors?.join(', ')}</FormHelperText>
      )}

      <FormHelperText component="div" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
        Can&apos;t find someone? Type their full email address (e.g.{' '}
        <strong>name@company.com</strong>) and press <strong>Enter</strong> to
        add them directly.
      </FormHelperText>

      <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}>
        {schema?.description ??
          'Add individual WellSky users (SSO, no invite) or external users (invite sent if new).'}
      </FormHelperText>

      {selected.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
            }}
          >
            <BusinessIcon
              sx={{
                fontSize: '0.9rem',
                color: ACCESS_KIND_STYLES.internal.color,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              WellSky (SSO login, no invite)
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
            }}
          >
            <PersonIcon
              sx={{
                fontSize: '0.9rem',
                color: ACCESS_KIND_STYLES.external.color,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              External (invite sent if new)
            </Typography>
          </Box>
        </Box>
      )}
    </FormControl>
  );
};

export const PublicAccessUserPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PublicAccessUserPicker',
    component: PublicAccessUserPicker,
  }),
);
